import { CommonTooltips } from "@mcleod/common";
import { Button, ClickEvent, Component, DataSourceExecutionEvent, DataSourceMode, Label, Layout, Panel, SaveButton, SlideoutDecorator, TableRow, TableRowDisplayEvent, Textbox } from "@mcleod/components";
import { ModelRow, RowUpdateEvent } from "@mcleod/core";
import { LMEGeneralSettings } from "@mcleod/general";
import { AutogenLayoutStopList } from "./autogen/AutogenLayoutStopList";
import { ClearStop } from "./ClearStop";
import { DispatchSlideout } from "./DispatchSlideout";
import { RowCallin } from "./models/ModelCallin";
import { OrderTabView } from "./OrderTabView";
import { ServiceIncident } from "./ServiceIncident";
import { StopNotesAndReferenceNumbers } from "./StopNotesAndReferenceNumbers";
import { Movement } from "./Movement";

export class StopList extends AutogenLayoutStopList {
    private _showCommentsAndReference = true;

    onLoad(): void | Promise<void> {
        this.sourceStop.orderBy = [{ field: "movement_sequence", sort: "asc" }];
    }

    /** This is an event handler for the onRowDisplay event of tableStop.  */
    tableStopOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow: TableRow = event.target as TableRow;
        const modelRow: ModelRow = tableRow.data as ModelRow;

        tableRow.table.dataSource.preventChangeNotifications = true;
        const locationName = tableRow.findComponentById("textboxLocationName") as Textbox;
        if (locationName != null) {
            locationName.quickInfoOverrideKeyValue = modelRow.get("location_id");
        }
        if ("A" === modelRow.get("status") && modelRow.get("actual_arrival") != null)
            tableRow.findComponentById("labelArrivedBadge").visible = true;
        if ("D" === modelRow.get("status")) {
            tableRow.findComponentById("labelDepartedBadge").visible = true;
        }
        const buttonEditArrivalDepart = tableRow.findComponentById("buttonEditArrivalDepart") as Button;
        if (buttonEditArrivalDepart != null)
            buttonEditArrivalDepart.visible = modelRow.get("status") === "D" || ("A" === modelRow.get("status") && modelRow.get("actual_arrival") != null);

        tableRow.findComponentById("labelAppointmentRequired").visible = modelRow.get("appt_required") === "Y" && modelRow.get("confirmed") === "N";
        tableRow.findComponentById("labelAppointmentConfirmed").visible = modelRow.get("appt_required") === "Y" && modelRow.get("confirmed") === "Y";
        const stopNotesAndReferenceLayout = tableRow.findComponentById((comp: any) => comp instanceof StopNotesAndReferenceNumbers) as StopNotesAndReferenceNumbers;
        stopNotesAndReferenceLayout.addLayoutLoadListener(() => {
            stopNotesAndReferenceLayout.stopRow = tableRow.data;
            stopNotesAndReferenceLayout.tableStopComments.allowEdit = false;
            stopNotesAndReferenceLayout.tableStopComments.displayToolsCell = false;
            stopNotesAndReferenceLayout.tableStopReferenceNumbers.allowEdit = false;
            stopNotesAndReferenceLayout.tableStopReferenceNumbers.displayToolsCell = false;
            if (!this._showCommentsAndReference) {
                stopNotesAndReferenceLayout.tabsetStopNotesRefNos.visible = false;
            }
        });

        const serviceIncidentLayout = tableRow.findComponentById((comp: any) => comp instanceof ServiceIncident) as ServiceIncident;
        serviceIncidentLayout.addLayoutLoadListener(() => {
            serviceIncidentLayout.visible = false;
            if (modelRow.get("service_fail") != null) {
                const sfRow = new ModelRow("lme/dispatch/servicefail", false, modelRow.get("service_fail"));
                serviceIncidentLayout.orderCustomerId = this.sourceStop.parentDataSource?.activeRow?.get("orders.customer_id");
                serviceIncidentLayout.sourceServicefail.setRowsAndMode(DataSourceMode.UPDATE, [sfRow], null);
                serviceIncidentLayout.visible = true;
                serviceIncidentLayout.sourceServicefail.parentDataSource = this.sourceStop.parentDataSource;
            }
        });

        if (this.sourceStop.parentDataSource?.activeRow?.get("manifest_id") != null) {
            const txtOrderId = tableRow.findComponentById("labelOrderId") as Label;
            const panelManifestOrder = tableRow.findComponentById("panelManifestOrder") as Panel;
            panelManifestOrder.visible = modelRow.get("order_id") != null;
            txtOrderId.visible = true;
            txtOrderId.addUnderlineWhenMouseOver();
            txtOrderId.addClickListener((event: ClickEvent) => {
                OrderTabView.navigateTo({ key: modelRow.get("order_id"), newTab: true });
            });
        }

        if (this.tableStop.dataSource.data.length == this.tableStop.rowCount)
            this.syncWidths();

        if (!modelRow.isNull("location_id")) {
            const panelLocation = tableRow.findComponentById("panelLocation") as Panel;
            CommonTooltips.setTooltipContentLayout(panelLocation, modelRow.get("location_id"), "lme/dispatch/LocationQuickInfo");
        }
    }

    private syncWidths() {
        const syncComps = new Map([["panelLocation", null], ["panelContact", null], ["panelDriverLoadUnload", null], ["panelDeliveryTimes", null], ["panelArriveDepart", null]])

        // get the widest component from the list of components from all table rows
        this.tableStop.rows.forEach(row => {
            syncComps.forEach((value, key) => {
                const comp = row.findComponentById(key);
                if (comp != null && (value == null || comp._element.clientWidth > value))
                    syncComps.set(key, comp._element.clientWidth);
            })
        })

        // set the width of the componenets on each row
        this.tableStop.rows.forEach(row => {
            syncComps.forEach((value, key) => {
                const comp = row.findComponentById(key);
                if (comp != null && value != null)
                    comp.width = value;
            })
        })
    }

    public set showCommentsAndReference(value: boolean) {
        this._showCommentsAndReference = value;
    }

    public get showCommentsAndReference(): boolean {
        return this._showCommentsAndReference;
    }

    /** This is an event handler for the onClick event of buttonEditCallinStop.  */
    buttonEditArrivalDepartOnClick(event: ClickEvent) {
        const buttonEditArrivalDepart = event.target as Button;
        buttonEditArrivalDepart.busy = true;
        const tableRow = TableRow.getContainingTableRow(event.target as Component);
        const stopType = (tableRow.findComponentById("textboxStopType") as Textbox).selectedItem.displayValue;
        const stopId = tableRow.data?.get("id");
        let showNextStopEta = false;
        if (tableRow.index + 1 < tableRow.table.rowCount) {
            const nextStopTableRow = tableRow.table.rows[tableRow.index + 1];
            showNextStopEta = "A" === nextStopTableRow.data.get("status")
        }
        if (stopId != null) {
            const saveBtn = SaveButton.create({ rowBreak: false });
            const layout = Layout.getLayout("lme/dispatch/DispatchSlideout") as DispatchSlideout;
            const decorator = new SlideoutDecorator({
                layout: layout,
                fillVerticalSpace: true,
                addlComponents: [saveBtn],
                width: 900,
                title: `${stopType} Arrival and Departure - Order ${tableRow.data.get("order_id")}`,
                doAfterSlideIn: () => buttonEditArrivalDepart.busy = false,
                onClose: () => decorator.slideOut(),
                layoutLoadListeners: (async () => {
                    const clearStop = layout.layoutClearStop as ClearStop;
                    saveBtn.dataSource = clearStop.sourceCallinStop;
                    layout.configureForArrivalDepartEdits(showNextStopEta);

                    clearStop.sourceCallinStop.search({ id: stopId }).then(result => {
                        clearStop.sourceCallinStop.mode = DataSourceMode.UPDATE;
                        clearStop.ordersCustomerId = this.sourceStop.parentDataSource?.activeRow?.get("orders.customer_id")
                        clearStop.sourceCallinStop.addAfterExecutionListener((event: DataSourceExecutionEvent) => {
                            decorator.slideOut().then(() => this.sourceStop.parentDataSource.search(this.sourceStop.parentDataSource.lastSearch));
                        })

                        clearStop.textboxActualArrival.required = true;
                        const dispatchControlSettings = LMEGeneralSettings.getSingleton().getDispatchControl();
                        if (dispatchControlSettings.require_departure == 'Y') {
                            clearStop.textboxActualDeparture.required = true;
                        }
                        if (result.modelRows[0]?.get("status") === "A" && this.owner instanceof Movement) {
                            clearStop.textboxActualArrival.required = false;
                            clearStop.textboxActualDeparture.required = false;
                            clearStop.textboxActualDeparture.enabled = false;
                        }
                        clearStop.sourceCallinStop.activeRow.addBeforePostListener((event: RowUpdateEvent) => {
                            return new Promise((resolve) => {
                                layout.isValid().then(isValid => {
                                    if (!isValid)
                                        event.preventDefault("Invalid Data", () => { });
                                    resolve(isValid);
                                })
                            })
                        })
                        clearStop.sourceCallin.setRowsAndMode(DataSourceMode.NONE, [new RowCallin()], () => {
                            if (showNextStopEta)
                                clearStop.calcNextStopEta();
                        });
                    });
                })
            });
        }
    }
}
